// ./src/utils/csvHelpers.ts
//
// Small, dependency-free helpers to build a CSV string and trigger a browser
// download. Values are escaped per RFC 4180 (quote when they contain a comma,
// quote, CR or LF; embedded quotes are doubled). A UTF-8 BOM is prepended so
// Excel opens accented characters correctly.

export type CsvCell = string | number | boolean | null | undefined;

/** Escape a single CSV cell, quoting only when necessary. */
export const escapeCsvCell = (value: CsvCell): string => {
  if (value === null || value === undefined) {
    return "";
  }

  const str = String(value);

  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }

  return str;
};

/**
 * A cell of TEXT SOMEONE ELSE TYPED (a visitor's name, an address), made safe for a spreadsheet: a value starting with
 * `=`, `+`, `-`, `@`, a tab or a CR is read by Excel / Sheets as a FORMULA (`=HYPERLINK(…)` runs on open — CSV
 * injection), so it gets a leading `'`, which the spreadsheet shows as text and hides. ⚠ Opt-in, per cell: a number the
 * export formats itself ("-12.50") must stay a number, so `buildCsv` does not do this to every cell.
 */
export const csvText = (value: string | null | undefined): string => {
  const str = value == null ? "" : String(value);

  return /^[=+\-@\t\r]/.test(str) ? `'${str}` : str;
};

/** Build a CSV string from a header row and data rows. */
export const buildCsv = (headers: CsvCell[], rows: CsvCell[][]): string => {
  const lines = [headers, ...rows].map((row) => row.map(escapeCsvCell).join(","));

  return lines.join("\r\n");
};

/**
 * Trigger a client-side download of `content` as `filename`. Adds a UTF-8 BOM
 * for spreadsheet compatibility. No-op outside the browser.
 */
export const downloadCsv = (filename: string, content: string): void => {
  if (typeof document === "undefined") {
    return;
  }

  const BOM = "\uFEFF"; // UTF-8 BOM so Excel detects encoding
  const blob = new Blob([BOM + content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
